import { latLonToUtm, utmCrs, utmToLatLon } from './utm.js';
import {
  createEnuFrame,
  enuToGeodetic,
  geodeticToEcef,
  geodeticToEnu
} from './world-coordinates.js';

function finite(value) {
  return Number.isFinite(Number(value));
}

export function createProjectSpatialFrame(projectCrs, worldAnchor) {
  if (projectCrs?.type !== 'utm' ||
      !finite(projectCrs.zone) ||
      !worldAnchor ||
      !finite(worldAnchor.latitude) ||
      !finite(worldAnchor.longitude)) return null;

  const crs = utmCrs(projectCrs.zone, projectCrs.hemisphere);
  const anchor = {
    latitude: Number(worldAnchor.latitude),
    longitude: Number(worldAnchor.longitude),
    height: Number(worldAnchor.height) || 0
  };

  return {
    version: 1,
    canonical: 'WGS84-ECEF',
    local: 'ENU',
    projectCrs: crs,
    anchor: {
      ...anchor,
      ecef: worldAnchor.ecef || geodeticToEcef(
        anchor.latitude,
        anchor.longitude,
        anchor.height
      )
    },
    enuFrame: createEnuFrame(
      anchor.latitude,
      anchor.longitude,
      anchor.height
    )
  };
}

export function projectToCanonical(x, y, z = 0, frame) {
  if (!frame || !finite(x) || !finite(y)) return null;
  const geodetic = utmToLatLon(
    Number(x),
    Number(y),
    frame.projectCrs.zone,
    frame.projectCrs.hemisphere
  );
  const height = Number(z) || 0;
  const ecef = geodeticToEcef(
    geodetic.latitude,
    geodetic.longitude,
    height
  );
  const enu = geodeticToEnu(
    geodetic.latitude,
    geodetic.longitude,
    height,
    frame.enuFrame
  );
  return {
    geodetic: { ...geodetic, height },
    ecef,
    enu
  };
}

export function canonicalToProject(enu, frame) {
  if (!frame ||
      !enu ||
      !finite(enu.east) ||
      !finite(enu.north) ||
      !finite(enu.up)) return null;
  const geodetic = enuToGeodetic(
    Number(enu.east),
    Number(enu.north),
    Number(enu.up),
    frame.enuFrame
  );
  const projected = latLonToUtm(
    geodetic.latitude,
    geodetic.longitude,
    frame.projectCrs.zone,
    frame.projectCrs.hemisphere
  );
  return {
    x: projected.easting,
    y: projected.northing,
    z: geodetic.height,
    geodetic
  };
}

export function canonicalBoundsFromProjectBounds(bounds, frame) {
  if (!bounds || !frame) return null;
  const minX = Number(bounds.minX), minY = Number(bounds.minY);
  const maxX = Number(bounds.maxX), maxY = Number(bounds.maxY);
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;

  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const minZ = Number(bounds.minZ) || 0;
  const maxZ = Number(bounds.maxZ) || minZ;
  const horizontalSamples = [
    [minX, minY], [centerX, minY], [maxX, minY],
    [minX, centerY], [centerX, centerY], [maxX, centerY],
    [minX, maxY], [centerX, maxY], [maxX, maxY]
  ];
  const samples = horizontalSamples.flatMap(([x, y]) => [
    projectToCanonical(x, y, minZ, frame)?.enu,
    projectToCanonical(x, y, maxZ, frame)?.enu
  ]).filter(Boolean);
  if (!samples.length) return null;

  return samples.reduce((result, point) => ({
    minEast: Math.min(result.minEast, point.east),
    minNorth: Math.min(result.minNorth, point.north),
    minUp: Math.min(result.minUp, point.up),
    maxEast: Math.max(result.maxEast, point.east),
    maxNorth: Math.max(result.maxNorth, point.north),
    maxUp: Math.max(result.maxUp, point.up)
  }), {
    minEast: Infinity,
    minNorth: Infinity,
    minUp: Infinity,
    maxEast: -Infinity,
    maxNorth: -Infinity,
    maxUp: -Infinity
  });
}
